import { getAllowedCountrySelectOptions } from "../../utils/general";
import { encodeTextForApi } from "../../utils/base64Utils";
import {
  sanitizeInAppNotificationHtml,
} from "../../utils/inAppNotificationUtils";
import {
  notificationTemplateTypes,
  notificationValidations,
  PUSH_MAX_CTAS,
  PUSH_SELECT_ALL_COUNTRIES,
  PUSH_TYPE_CATEGORY_MAP,
  DEFAULT_PUSH_TARGET_SCREEN,
  PUSH_TARGET_SCREEN_PREFIX,
} from "./notificationConstant";

export function stripPushTargetScreenPrefix(value = "") {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  if (trimmed.toLowerCase().startsWith(PUSH_TARGET_SCREEN_PREFIX)) {
    return trimmed.slice(PUSH_TARGET_SCREEN_PREFIX.length);
  }
  return trimmed;
}

export function withPushTargetScreenPrefix(path = "") {
  const stripped = stripPushTargetScreenPrefix(path);
  if (!stripped) return "";
  return `${PUSH_TARGET_SCREEN_PREFIX}${stripped}`;
}

export function resolvePushTargetScreen(value) {
  const full = withPushTargetScreenPrefix(value);
  return full || DEFAULT_PUSH_TARGET_SCREEN;
}

export function csvToArray(str, delimiter = ",") {
  if (!str || typeof str !== "string" || !str.includes("\n")) {
    return [];
  }
  const headers = str.slice(0, str.indexOf("\n")).split(delimiter);
  const rows = str.slice(str.indexOf("\n") + 1).split("\n");
  if (rows && rows.length > 0 && rows[rows.length - 1] === "") {
    rows.splice(-1);
  }
  if (!rows || rows.length === 0) {
    return [];
  }
  return rows.map(function (row) {
    const values = row.split(delimiter);
    return headers.reduce(function (object, header, index) {
      object[header] = values[index];
      return object;
    }, {});
  });
}

function cleanCsvCell(value = "") {
  return String(value)
    .replace(/^\uFEFF/, "")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/\u00a0/g, " ")
    .trim()
    .replace(/^["']+|["']+$/g, "")
    .trim();
}

function extractUserIdFromCell(value = "") {
  const cleaned = cleanCsvCell(value);
  if (!cleaned) return "";
  const match = cleaned.match(
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i
  );
  return match ? match[0] : "";
}

export function parseCsvUserIdsFromText(textData = "") {
  const userIds = [];
  const lines = String(textData || "")
    .replace(/^\uFEFF/, "")
    .split(/\r\n|\n|\r/);

  for (let i = 0; i < lines.length; i += 1) {
    const cell = (lines[i].split(/[,;\t]/)[0] || "");
    const userId = extractUserIdFromCell(cell);
    if (userId) {
      userIds.push(userId);
    }
  }

  return userIds;
}

export function parseCsvUserIdsFromFile(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      resolve([]);
      return;
    }
    const reader = new FileReader();
    reader.onload = function (event) {
      resolve(parseCsvUserIdsFromText(event.target.result));
    };
    reader.onerror = () => reject(reader.error || new Error("Failed to read CSV file"));
    reader.readAsText(file);
  });
}

export function stripHtmlToPlainText(html = "") {
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

export function getNotificationMessageForDisplay(notification = {}) {
  const message = notification?.message;
  if (!message || typeof message !== "object") return {};

  if (message.payload != null) {
    return message.payload;
  }

  const emailMessage = {};
  if (message.subject != null && String(message.subject).trim() !== "") {
    emailMessage.subject = message.subject;
  }
  if (message.text != null && String(message.text).trim() !== "") {
    emailMessage.text = message.text;
  }
  if (message.html != null && String(message.html).trim() !== "") {
    emailMessage.html = message.html;
  }
  return emailMessage;
}

function toCount(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function getNotificationProcessCounts(details = {}) {
  const tps = details?.status?.taskProcessStatus ?? {};
  const nps = details?.status?.notificationProcessStatus ?? {};

  const successCount =
    toCount(tps.notificationCount) ||
    toCount(nps.notificationSentCount) ||
    toCount(nps.userSentCount);

  const failureCount =
    toCount(tps.userFailedCount) +
    toCount(nps.failedUserSentCount) +
    toCount(nps.tokenNotFoundUserCount);

  return {
    successCount,
    failureCount,
    totalCount: successCount + failureCount,
  };
}

export function toNotificationExpiryEndOfDay(date) {
  if (!date) return null;
  const expiry = new Date(date);
  expiry.setHours(23, 59, 59, 0);
  return expiry.getTime();
}

export function isNotificationExpiryInFuture(expiryDateMs) {
  return Boolean(expiryDateMs && expiryDateMs > Date.now());
}

export function getNotificationExpiryUnixSeconds(expiryDateMs) {
  if (!expiryDateMs) return null;
  return Math.floor(expiryDateMs / 1000);
}

export function getPushCountryCodes(allowedCountries = []) {
  return getAllowedCountrySelectOptions(allowedCountries, {
    includeNone: false,
  }).map((option) => option.value);
}

export function normalizePushCountrySelection(nextValue = [], allCountryCodes = []) {
  const selected = (Array.isArray(nextValue) ? nextValue : []).filter(
    (value) => value && value !== "None"
  );
  const codes = Array.isArray(allCountryCodes) ? allCountryCodes : [];

  if (selected.includes(PUSH_SELECT_ALL_COUNTRIES)) {
    const withoutAll = selected.filter(
      (value) => value !== PUSH_SELECT_ALL_COUNTRIES
    );
    const allSelected =
      codes.length > 0 && codes.every((code) => withoutAll.includes(code));
    return allSelected ? [] : [...codes];
  }

  return selected.filter((value) => codes.includes(value));
}

export function cleanPushCtas(ctas = []) {
  const labelMax = notificationValidations.push.ctaLabel.maxLength;
  const targetMax = notificationValidations.push.ctaTarget.maxLength;
  return (Array.isArray(ctas) ? ctas : [])
    .filter((cta) => cta?.label?.trim() && cta?.target?.trim())
    .slice(0, PUSH_MAX_CTAS)
    .map((cta, index) => ({
      action: "Open",
      id: `button${index + 1}`,
      text: cta.label.trim().slice(0, labelMax),
      targetScreen: cta.target.trim().slice(0, targetMax),
    }));
}

export function buildPushMessagePayload(form = {}, expiryDateMs) {
  const descriptionHtml = sanitizeInAppNotificationHtml(form.description || "");
  const imageUrl = (form.image || "").trim();
  const title = sanitizeInAppNotificationHtml(form.title || "").trim()
    || (form.title || "").trim();
  const targetScreen = resolvePushTargetScreen(form.targetScreen);

  const payload = {
    type: (form.type || "").trim(),
    category: (form.category || "").trim(),
    title,
    description: encodeTextForApi(descriptionHtml),
    targetScreen,
  };

  if (imageUrl) {
    payload.imageUrl = imageUrl;
  }

  const ts = getNotificationExpiryUnixSeconds(expiryDateMs);
  if (ts != null) {
    payload.ts = String(ts);
  }

  const priority = (form.priority || "").trim();
  if (priority) {
    payload.priority = priority;
  }

  const cleanedCtas = cleanPushCtas(form.ctas);
  if (cleanedCtas.length > 0) {
    payload.ctas = JSON.stringify(cleanedCtas);
  }

  return payload;
}

export function validatePushMessageForm(form = {}) {
  if (!(form.type || "").trim()) return "Please select Type";
  if (!(form.category || "").trim()) return "Please select Category";
  const allowedCategories = PUSH_TYPE_CATEGORY_MAP[form.type] || [];
  if (!allowedCategories.includes(form.category)) {
    return "Please select a valid Category for the selected Type";
  }
  if (!(form.title || "").trim()) return "Push notification title cannot be empty";
  const titleMax = notificationValidations.push.title.maxLength;
  if ((form.title || "").length > titleMax) {
    return `Push notification title cannot exceed ${titleMax} characters`;
  }

  const description = form.description || "";
  const hasText = Boolean(stripHtmlToPlainText(description));
  if (!hasText) {
    return "Push notification description cannot be empty";
  }
  const bodyMax = notificationValidations.push.body.maxLength;
  if (description.length > bodyMax) {
    return `Push notification description cannot exceed ${bodyMax} characters`;
  }

  const ctas = Array.isArray(form.ctas) ? form.ctas : [];
  if (ctas.length > PUSH_MAX_CTAS) {
    return `A maximum of ${PUSH_MAX_CTAS} CTAs is allowed`;
  }
  for (let i = 0; i < ctas.length; i += 1) {
    const label = ctas[i]?.label?.trim() || "";
    const target = ctas[i]?.target?.trim() || "";
    if ((label && !target) || (!label && target)) {
      return `CTA ${i + 1} requires both Text and Target Screen`;
    }
  }
  return null;
}

export function getOptionLabel(options = [], value, emptyLabel = "") {
  if (!value) return emptyLabel;
  return options.find((option) => option.value === value)?.label || value;
}

export function withCurrentSelectOption(options = [], value) {
  if (!value) return options;
  if (options.some((option) => option.value === value)) return options;
  return [...options, { value, label: value }];
}

export function clonePlain(value) {
  if (value == null) return value;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (_) {
    return value;
  }
}

export function getTemplateId(template = {}) {
  return template.templateId || template.id || "";
}

export function resolveLoadedTemplateValue(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (/^\{\{[^}]+\}\}$/.test(raw)) return "";
  const plain = stripHtmlToPlainText(raw);
  if (/^\{\{[^}]+\}\}$/.test(plain)) return "";
  return value;
}

function slugifyCtaId(text, index) {
  const slug = String(text || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
  return slug || `cta_${index + 1}`;
}

const parseJsonValue = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed || (trimmed[0] !== "{" && trimmed[0] !== "[")) return value;
  try {
    return JSON.parse(trimmed);
  } catch (_) {
    return value;
  }
};

const resolveTemplateChannel = (apiTemplate = {}) => {
  const raw = apiTemplate.channel || apiTemplate.notificationType || "";
  if (typeof raw === "string") return raw;
  return raw?.type || raw?.value || "";
};

const toTemplateTimestamp = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "number" || /^\d+$/.test(String(value).trim())) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) return "";
    const ms = numeric < 1e12 ? numeric * 1000 : numeric;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? "" : date.toISOString();
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toISOString();
};

export function toUiCtas(ctas = []) {
  const parsed = parseJsonValue(ctas);
  const list = Array.isArray(parsed) ? parsed : [];
  return list.map((cta, index) => ({
    id: cta?.id || slugifyCtaId(cta?.text || cta?.label, index),
    label: cta?.text || cta?.label || "",
    action: cta?.action || "open_url",
    target: cta?.target || cta?.targetScreen || "",
  }));
}

export function toApiCtas(ctas = []) {
  return (Array.isArray(ctas) ? ctas : [])
    .filter((cta) => (cta?.label || cta?.text || "").trim() || (cta?.target || "").trim())
    .map((cta, index) => ({
      id: cta.id || slugifyCtaId(cta.label || cta.text, index),
      text: (cta.label || cta.text || "").trim(),
      action: cta.action || "open_url",
      target: (cta.target || "").trim(),
    }));
}

export function normalizeTemplateContent(notificationType, content = {}) {
  const source = parseJsonValue(content) || {};
  if (notificationType === notificationTemplateTypes.email.value) {
    return {
      subject: source.subject || source.title || "",
      html: source.html || source.description || source.body || "",
    };
  }
  if (notificationType === notificationTemplateTypes.sms.value) {
    return {
      body: source.body || source.text || source.description || "",
    };
  }
  return {
    severity: source.severity || "",
    type: source.type || "",
    category: source.category || "",
    title: source.title || "",
    description: source.description || source.body || "",
    targetScreen: source.targetScreen || "",
    image: source.image || source.imageUrl || "",
    ctas: toUiCtas(source.ctas),
  };
}

export function toUiTemplate(apiTemplate = {}) {
  const channel = resolveTemplateChannel(apiTemplate);
  const content = normalizeTemplateContent(channel, apiTemplate.content);
  if (channel === notificationTemplateTypes.push.value) {
    content.severity = apiTemplate.severity || content.severity;
    content.type = apiTemplate.type || content.type;
    content.category = apiTemplate.category || content.category;
  }
  const templateId = apiTemplate.templateId || apiTemplate.id || "";
  return {
    id: templateId,
    templateId,
    templateName: apiTemplate.name || apiTemplate.templateName || "",
    notificationType: channel,
    createdAt: toTemplateTimestamp(apiTemplate.createdAt),
    lastUpdated: toTemplateTimestamp(apiTemplate.updatedAt || apiTemplate.lastUpdated),
    createdBy: apiTemplate.createdBy || "",
    updatedBy: apiTemplate.updatedBy || "",
    content,
  };
}

export function buildNotificationTemplatePayload(template = {}) {
  const channel = template.notificationType;
  const name = (template.templateName || "").trim();
  const content = normalizeTemplateContent(channel, template.content);

  if (channel === notificationTemplateTypes.push.value) {
    const targetScreen = withPushTargetScreenPrefix(content.targetScreen);
    const imageUrl = (content.image || "").trim();
    const ctas = toApiCtas(content.ctas);
    const pushContent = {
      title: content.title,
      description: content.description,
    };
    if (targetScreen) pushContent.targetScreen = targetScreen;
    if (imageUrl) pushContent.imageUrl = imageUrl;
    if (ctas.length) pushContent.ctas = ctas;
    return {
      name,
      channel,
      severity: content.severity,
      type: content.type,
      category: content.category,
      content: pushContent,
    };
  }

  if (channel === notificationTemplateTypes.email.value) {
    return {
      name,
      channel,
      content: {
        subject: content.subject,
        html: content.html,
      },
    };
  }

  if (channel === notificationTemplateTypes.sms.value) {
    return {
      name,
      channel,
      content: {
        text: content.body,
      },
    };
  }

  return {
    name,
    channel,
    content,
  };
}

export function validateNotificationTemplate(template = {}) {
  if (!(template.templateName || "").trim()) return "Template Name is required";
  const nameMax = notificationValidations.templateName.maxLength;
  if ((template.templateName || "").trim().length > nameMax) {
    return `Template Name cannot exceed ${nameMax} characters`;
  }
  const type = template.notificationType;
  const content = normalizeTemplateContent(type, template.content);

  if (type === notificationTemplateTypes.push.value) {
    if (!(content.severity || "").trim()) return "Please select Severity";
    return validatePushMessageForm(content);
  }
  if (type === notificationTemplateTypes.email.value) {
    if (!(content.subject || "").trim()) return "Email notification subject cannot be empty";
    const subjectMax = notificationValidations.email.subject.maxLength;
    if ((content.subject || "").length > subjectMax) {
      return `Email notification subject cannot exceed ${subjectMax} characters`;
    }
    if (!stripHtmlToPlainText(content.html || "") && !(content.html || "").includes("<img")) {
      return "Email notification body cannot be empty";
    }
    const bodyMax = notificationValidations.email.body.maxLength;
    if ((content.html || "").length > bodyMax) {
      return `Email notification body cannot exceed ${bodyMax} characters`;
    }
    return null;
  }
  if (type === notificationTemplateTypes.sms.value) {
    if (!(content.body || "").trim()) return "SMS message cannot be empty";
    const smsMax = notificationValidations.sms.body.maxLength;
    if ((content.body || "").length > smsMax) {
      return `SMS message cannot exceed ${smsMax} characters`;
    }
    return null;
  }
  return "Please select a notification type";
}

export function mapTemplateToPushForm(template = {}) {
  const mapped = toUiTemplate(template);
  const content = mapped.content || {};
  return {
    type: resolveLoadedTemplateValue(content.type),
    category: resolveLoadedTemplateValue(content.category),
    title: resolveLoadedTemplateValue(content.title),
    description: resolveLoadedTemplateValue(content.description),
    targetScreen: resolveLoadedTemplateValue(content.targetScreen),
    image: resolveLoadedTemplateValue(content.image),
    ctas: (content.ctas || [])
      .map((cta) => ({
        ...cta,
        label: resolveLoadedTemplateValue(cta.label),
        target: resolveLoadedTemplateValue(cta.target),
      }))
      .filter((cta) => (cta.label || "").trim() || (cta.target || "").trim()),
    severity: resolveLoadedTemplateValue(content.severity),
  };
}

export function mapTemplateToEmailForm(template = {}) {
  const mapped = toUiTemplate(template);
  const content = mapped.content || {};
  return {
    subject: resolveLoadedTemplateValue(content.subject),
    html: resolveLoadedTemplateValue(content.html),
  };
}
